<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  onMounted,
  defineEmits,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

import { Virtualizer } from 'virtua/vue';
import ChatListHeader from './ChatListHeader.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import ConversationItem from './ConversationItem.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import TeamSelectionModal from './widgets/conversation/TeamSelectionModal.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import ConversationSearchModal from './widgets/conversation/ConversationSearchModal.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';
import AssignAndResolveDialog from 'dashboard/components-next/Conversation/AssignAndResolveDialog.vue';
import { useResolveAssignmentGate } from 'dashboard/composables/useResolveAssignmentGate';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useReplyAssignmentGate } from 'dashboard/composables/useReplyAssignmentGate';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import { emitter } from 'shared/helpers/mitt';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { losesConversationAccess } from 'dashboard/helper/conversationAccessHelper';
import {
  conversationListPageURL,
  conversationUrl,
  frontendURL,
} from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnUnattendedView,
  isOnUnreadView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import {
  ASSIGNEE_TYPE_TAB_PERMISSIONS,
  TEAM_SUPERVISOR,
} from 'dashboard/constants/permissions.js';
import {
  UNREAD_ROUTES,
  resolveAssigneeTab,
} from 'dashboard/helper/unreadViewHelper';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  conversationStatus: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const resolveAttributesModalRef = ref(null);
const conversationListRef = ref(null);
const virtualListRef = ref(null);

provide('contextMenuElementTarget', virtualListRef);

// activeAssigneeTab is initialized after assigneeTabItems (below) so the default
// can be clamped to the tabs the user is actually allowed to see.
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const showAdvancedFilters = ref(false);
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const isContextMenuOpen = ref(false);
const showSearchModal = ref(false);
// Local cache for search results — completely isolated from Vuex lifecycle
// Only cleared by explicit user action (empty input, X button, Escape)
const cachedSearchResults = ref([]);
const cachedSearchQuery = ref('');
const appliedFilter = ref([]);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const aiChatsList = useMapGetter('getAIChats');
const awaitingChatsList = useMapGetter('getAwaitingChats');
const rodizioChatsList = useMapGetter('getRodizioChats');
const pendingClosureChatsList = useMapGetter('getPendingClosureChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const chatListError = useMapGetter('getChatListError');
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentRole = useMapGetter('getCurrentRole');
const myTeams = useMapGetter('teams/getMyTeams');
// Membro ∪ supervisionado — só para a guarda de acesso à conversa. Os diálogos
// de "assumir e encerrar" continuam com myTeams: atribuir a si mesmo em time do
// qual não se é membro seria desfeito por ensure_assignee_is_from_team.
const visibleTeams = useMapGetter('teams/getVisibleTeams');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');
const getConversationById = useMapGetter('getConversationById');

// AI tabs (Fase C — C4): gated by the reply_assignment_gate account setting.
// A conta alterna entre as filas antigas e Rodízio/Atenção; ambas continuam
// subordinadas à Atribuição obrigatória.
const { isAssignmentGateEnabled, isRodizioTabsEnabled } =
  useReplyAssignmentGate();

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onRemoveLabels,
  onAssignTeamsForBulk,
  onUpdateConversations,
  showTransferTeamModal,
  teams,
  onConfirmAgentTransferTeam,
  cancelAgentTransferTeamSelection,
  showBulkAssignResolveDialog,
  bulkAssignResolveRequiresTeam,
  bulkAssignResolveCount,
  onConfirmBulkAssignResolve,
  onCancelBulkAssignResolve,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

const { checkMissingAttributes, splitAttributesByModel } =
  useConversationRequiredAttributes();
// Definições mostradas no modal — guardadas para separar, na volta, o que é da
// conversa do que é do contato.
const pendingRequiredAttributes = ref([]);

const {
  requiresAssignmentToResolve,
  getResolveVariant,
  getAssignee,
  hasTeam,
  assignSelfForResolve,
} = useResolveAssignmentGate();

// "Atribuição obrigatória" — assign-and-resolve dialog state for the context menu.
const showAssignResolveDialog = ref(false);
const assignResolveContext = ref({
  conversationId: null,
  snoozedUntil: null,
  variant: 'unassigned',
  assigneeName: '',
  requiresTeam: false,
});

function openAssignResolveDialog(conversation, snoozedUntil) {
  const variant = getResolveVariant(conversation);
  const assignee = getAssignee(conversation);
  assignResolveContext.value = {
    conversationId: conversation.id,
    snoozedUntil,
    variant,
    assigneeName:
      variant === 'ai'
        ? assignee?.name ||
          t('CONVERSATION.RESOLVE_ASSIGNMENT_GATE.AI_FALLBACK')
        : assignee?.name || '',
    requiresTeam: !hasTeam(conversation),
  };
  showAssignResolveDialog.value = true;
}

function closeAssignResolveDialog() {
  showAssignResolveDialog.value = false;
}

// computed

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

// Keys the user is allowed to use by *permission* (role/custom-role), before
// any UI-level filtering of the tab-bar. The gate ON hides the "unassigned"
// chip from the tab-bar (it lives in the sidebar instead), but the user may
// still legitimately land on the unassigned queue via that sidebar item /
// deep-link. Clamping must respect permission, not tab-bar visibility — so
// resolveAssigneeTab uses these keys, keeping the security clamp intact for
// roles that genuinely lack the permission.
// Admin ou supervisor: vê o rodízio de todo mundo e a aba "Atenção".
const isSupervisorView = computed(() =>
  ['administrator', TEAM_SUPERVISOR].some(p =>
    userPermissions.value.includes(p)
  )
);

const permittedAssigneeTabKeys = computed(() =>
  filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  )
    .map(({ key }) => key)
    .filter(key => {
      if (key === wootConstants.ASSIGNEE_TYPE.AWAITING)
        return isAssignmentGateEnabled.value && !isRodizioTabsEnabled.value;
      if (key === wootConstants.ASSIGNEE_TYPE.RODIZIO)
        return isRodizioTabsEnabled.value;
      if (key === wootConstants.ASSIGNEE_TYPE.PENDING_CLOSURE)
        return (
          isAssignmentGateEnabled.value &&
          (!isRodizioTabsEnabled.value || isSupervisorView.value)
        );
      return true;
    })
);

const assigneeTabItems = computed(() => {
  const useUnattendedCounts = props.conversationType === 'unattended';
  const countKeyForTab = key => {
    if (useUnattendedCounts) {
      if (key === wootConstants.ASSIGNEE_TYPE.ME)
        return 'unattendedAssignedCount';
      if (key === wootConstants.ASSIGNEE_TYPE.UNASSIGNED)
        return 'unattendedUnassignedCount';
      if (key === wootConstants.ASSIGNEE_TYPE.ALL) return 'allCount';
    }
    return ASSIGNEE_TYPE_TAB_PERMISSIONS[key]?.count ?? 'allCount';
  };
  const stats = conversationStats.value;
  const iconForKey = key => {
    if (key === wootConstants.ASSIGNEE_TYPE.ME) return 'i-lucide-user';
    if (key === wootConstants.ASSIGNEE_TYPE.AWAITING) return 'i-lucide-clock';
    if (key === wootConstants.ASSIGNEE_TYPE.RODIZIO)
      return 'i-lucide-refresh-cw';
    if (key === wootConstants.ASSIGNEE_TYPE.UNASSIGNED)
      return 'i-lucide-user-round-x';
    if (key === wootConstants.ASSIGNEE_TYPE.AI) return 'i-lucide-sparkles';
    if (key === wootConstants.ASSIGNEE_TYPE.PENDING_CLOSURE)
      return isRodizioTabsEnabled.value
        ? 'i-lucide-triangle-alert'
        : 'i-lucide-check-check';
    if (key === wootConstants.ASSIGNEE_TYPE.ALL) return 'i-lucide-users';
    return null;
  };
  const items = filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  )
    .filter(({ key }) => {
      // Dynamic tabs (modo IA / gate ON): só aparecem quando têm conteúdo.
      if (key === wootConstants.ASSIGNEE_TYPE.AWAITING)
        return (
          !isRodizioTabsEnabled.value &&
          isAssignmentGateEnabled.value &&
          !useUnattendedCounts &&
          (stats.awaitingCount ?? 0) > 0
        );
      // "Rodízio": gate ON, fora da visão de não-respondidas. Admin/supervisor
      // sempre; agente só quando tem conversa na vez dele.
      if (key === wootConstants.ASSIGNEE_TYPE.RODIZIO)
        return (
          isRodizioTabsEnabled.value &&
          isAssignmentGateEnabled.value &&
          !useUnattendedCounts &&
          (isSupervisorView.value || (stats.rodizioCount ?? 0) > 0)
        );
      // "IA": gate ON + ai_count > 0 (antes dependia de hasCaptainAgents — agora
      // a aba só aparece quando de fato há conversas com a IA), fora da visão de
      // não-respondidas (sem contadores per-AI — OQ-2).
      if (key === wootConstants.ASSIGNEE_TYPE.AI)
        return (
          isAssignmentGateEnabled.value &&
          !useUnattendedCounts &&
          (stats.aiCount ?? 0) > 0
        );
      // "Atenção" (a permissão já restringe a admin/supervisor): gate ON + count > 0.
      if (key === wootConstants.ASSIGNEE_TYPE.PENDING_CLOSURE)
        return (
          isAssignmentGateEnabled.value &&
          !useUnattendedCounts &&
          (!isRodizioTabsEnabled.value || isSupervisorView.value) &&
          (stats.awaitingClosureCount ?? 0) > 0
        );
      // Gate ON: "Não atribuídas" deixa de ser chip da tab-bar no modo IA
      // (a fila vira "Aguardando atendimento"). Modo clássico mantém
      // "Não atribuídas" como aba normal (return true abaixo).
      if (
        key === wootConstants.ASSIGNEE_TYPE.UNASSIGNED &&
        isAssignmentGateEnabled.value &&
        !useUnattendedCounts
      )
        return false;
      return true;
    })
    .map(({ key }) => {
      return {
        key,
        name: t(
          `CHAT_LIST.ASSIGNEE_TYPE_TABS.${
            key === wootConstants.ASSIGNEE_TYPE.PENDING_CLOSURE &&
            isRodizioTabsEnabled.value
              ? 'attention'
              : key
          }`
        ),
        count: stats[countKeyForTab(key)] ?? 0,
        icon: iconForKey(key),
        // "Rodízio" recebe o efeito de atenção em vermelho (.tab-alert):
        // é a vez de alguém pegar o lead.
        variant:
          key === wootConstants.ASSIGNEE_TYPE.RODIZIO ||
          key === wootConstants.ASSIGNEE_TYPE.AWAITING
            ? 'alert'
            : undefined,
      };
    });
  return items;
});

// Route-aware default tab, clamped to the tabs the user is allowed to use by
// permission (not just the ones rendered in the tab-bar) so the dedicated
// "Aguardando atendimento" route / sidebar deep-link can select the unassigned
// tab even though the gate hides its chip from the tab-bar.
const activeAssigneeTab = ref(
  resolveAssigneeTab(route.name, route.query, permittedAssigneeTabKeys.value)
);

const showAssigneeInConversationCard = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.AI
  );
});

const currentPageFilterKey = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  // Optional chaining: the conditional "Unassigned" tab can vanish while
  // still active for one tick before the fallback watcher kicks in.
  const count = assigneeTabItems.value.find(
    item => item.key === activeAssigneeTab.value
  )?.count;
  return count ?? 0;
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const visibleChats = Array.isArray(chatsOnView.value)
    ? chatsOnView.value
    : [];
  const visibleCount = visibleChats.length;
  const tabCount = activeAssigneeTabCount.value;

  const isChatListEmpty = visibleCount === 0;
  const isDefaultView = !hasAppliedFiltersOrActiveFolders.value;
  // The rendered list is empty (e.g. right after a tab switch/reset) but the
  // tab's total count says there is still a partial page (1..24) left to load.
  // Re-fetch from page 1 instead of advancing the cursor past the data.
  const hasUnloadedPartialPage =
    tabCount > visibleCount && tabCount < conversationsPerPage;

  if (isDefaultView && isChatListEmpty && hasUnloadedPartialPage) {
    return 1;
  }

  return currentPage.value + 1;
});

const dateRangeFilter = ref(null);

const conversationFilters = computed(() => {
  const filterKey = activeAssigneeTab.value;
  const nextCursor =
    store.getters['conversationPage/getNextCursor']?.(filterKey);
  return {
    inboxId: props.conversationInbox ? props.conversationInbox : undefined,
    assigneeType: activeAssigneeTab.value,
    status: activeStatus.value,
    sortBy: activeSortBy.value,
    // `page` vai sempre: o ramo keyset do backend o ignora, e mandar `undefined`
    // gravava currentPage=undefined — que virava NaN se o cursor sumisse no meio da
    // rolagem, fazendo a lista repetir a pagina 1 para sempre.
    page: conversationListPagination.value,
    cursor: nextCursor || undefined,
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    conversationType: props.conversationType || undefined,
    dateFrom: dateRangeFilter.value?.from || undefined,
    dateTo: dateRangeFilter.value?.to || undefined,
  };
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const searchContext = computed(() => {
  return {
    teamId: props.teamId || undefined,
    inboxId: props.conversationInbox || undefined,
    label: props.label || undefined,
    conversationType: props.conversationType || undefined,
    foldersId: props.foldersId || undefined,
  };
});

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === 'mention') {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (props.conversationType === 'participating') {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === 'unattended') {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (props.conversationType === 'unread') {
    return t('CHAT_LIST.UNREAD_HEADING');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

const searchResults = computed(
  () => store.getters['conversationSearch/getConversationRecords'] || []
);

const conversationSearchQuery = computed(
  () => store.getters['conversationSearch/getConversationSearchQuery'] || ''
);

const isSearchActive = computed(() => {
  const active =
    cachedSearchQuery.value.length > 0 || cachedSearchResults.value.length > 0;
  return active;
});

// Mostrar empty state "nenhum resultado" só depois que a API de busca respondeu (não antes nem durante o loading)
const conversationSearchCompleted = computed(
  () =>
    store.getters['conversationSearch/getConversationSearchCompleted'] || false
);

// Sync Vuex search results to local cache when new results arrive
watch(searchResults, newResults => {
  if (newResults && newResults.length > 0) {
    cachedSearchResults.value = [...newResults];
  }
});

// Sync Vuex search query to local cache
watch(conversationSearchQuery, newQuery => {
  if (newQuery && newQuery.trim().length > 0) {
    cachedSearchQuery.value = newQuery.trim();
  }
});

const showSearchNoResults = computed(() => {
  return (
    cachedSearchQuery.value.length > 0 &&
    conversationSearchCompleted.value &&
    cachedSearchResults.value.length === 0
  );
});

const conversationList = computed(() => {
  // Search mode: use local cached results (immune to Vuex/WebSocket/route changes)
  if (isSearchActive.value) {
    let filteredResults = [...cachedSearchResults.value];

    // Deduplicar por contato — mostrar só a conversa mais recente de cada contato
    const byContact = new Map();
    filteredResults.forEach(conv => {
      const contactId =
        conv.contact?.id || conv.meta?.sender?.id || `conv_${conv.id}`;
      const existing = byContact.get(contactId);
      if (!existing) {
        byContact.set(contactId, conv);
      } else {
        const existingTime =
          existing.last_activity_at || existing.created_at || 0;
        const currentTime = conv.last_activity_at || conv.created_at || 0;
        if (currentTime > existingTime) {
          byContact.set(contactId, conv);
        }
      }
    });
    return Array.from(byContact.values());
  }

  // Lógica original quando busca não está ativa
  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    if (activeAssigneeTab.value === 'me') {
      localConversationList = mineChatsList.value;
    } else if (activeAssigneeTab.value === 'unassigned') {
      localConversationList = unAssignedChatsList.value;
    } else if (activeAssigneeTab.value === 'awaiting') {
      localConversationList = awaitingChatsList.value;
    } else if (activeAssigneeTab.value === 'rodizio') {
      localConversationList = rodizioChatsList.value;
    } else if (activeAssigneeTab.value === 'pending_closure') {
      localConversationList = pendingClosureChatsList.value;
    } else if (activeAssigneeTab.value === 'ai') {
      localConversationList = aiChatsList.value;
    } else {
      localConversationList = allChatList.value;
    }
  } else {
    localConversationList = chatLists.value;
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  return localConversationList;
});

const showEndOfListMessage = computed(() => {
  return (
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value &&
    !chatListError.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { status, order_by: orderBy } = filterBy;
  // Prioritize conversationStatus prop from route over saved filter
  activeStatus.value =
    props.conversationStatus || status || wootConstants.STATUS_TYPE.OPEN;
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    orderBy
  )
    ? orderBy
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

function fetchConversations() {
  store.dispatch('updateChatListFilters', conversationFilters.value);
  store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

function resetAndFetchData() {
  appliedFilter.value = [];
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  // Force refresh tab counts when switching views/routes
  // The /meta endpoint is lightweight (counts only), safe to run in parallel
  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filtersForMeta = { ...conversationFilters.value };
    delete filtersForMeta.assigneeType;
    filtersForMeta.force = true;
    store.dispatch('conversationStats/get', filtersForMeta);
  }
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations();
}

function retryConversations() {
  if (hasActiveFolders.value) {
    fetchSavedFilteredConversations(activeFolder.value.query);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  } else {
    fetchConversations();
  }
}

function loadMoreConversations() {
  // Não carregar mais a lista normal enquanto a busca estiver ativa
  if (isSearchActive.value) {
    return;
  }
  if (
    hasCurrentPageEndReached.value ||
    chatListLoading.value ||
    chatListError.value
  ) {
    return;
  }

  retryConversations();
}

// Use IntersectionObserver instead of @scroll since Virtualizer only emits on user scroll.
// If the list doesn’t fill the viewport, loading can stall.
// IntersectionObserver triggers as soon as the sentinel is visible.
const intersectionObserverOptions = computed(() => ({
  root: conversationListRef.value,
  rootMargin: '100px 0px 100px 0px',
}));

function updateAssigneeTab(selectedTab) {
  if (props.conversationType === 'unattended') {
    let query;
    if (selectedTab === wootConstants.ASSIGNEE_TYPE.UNASSIGNED) {
      query = { assignee_type: 'unassigned' };
    } else if (selectedTab === wootConstants.ASSIGNEE_TYPE.ALL) {
      query = { assignee_type: 'all' };
    }
    router.push({
      name: 'conversation_unattended',
      params: { accountId: currentAccountId.value },
      ...(query && { query }),
    });
    return;
  }
  // Unread: keep the active tab reflected in the URL (?assignee_type=...) for
  // all three tabs, so the address bar always matches the highlighted tab and
  // the view is deep-linkable. The deep-link watcher then syncs the tab state
  // and refetches. Always explicit (unlike unattended, whose default is "me").
  if (props.conversationType === 'unread') {
    const queryByTab = {
      [wootConstants.ASSIGNEE_TYPE.ME]: 'me',
      [wootConstants.ASSIGNEE_TYPE.UNASSIGNED]: 'unassigned',
      [wootConstants.ASSIGNEE_TYPE.ALL]: 'all',
    };
    router.push({
      name: 'conversation_unread',
      params: { accountId: currentAccountId.value },
      query: { assignee_type: queryByTab[selectedTab] ?? 'all' },
    });
    return;
  }
  if (activeAssigneeTab.value !== selectedTab) {
    resetBulkActions();
    emitter.emit('clearSearchInput');
    activeAssigneeTab.value = selectedTab;
    store.dispatch('conversationPage/reset');
    store.dispatch('emptyAllConversations');
    fetchConversations();
  }
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
  } else {
    activeSortBy.value = value;
  }
  resetAndFetchData();
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = 'mention';
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = 'unattended';
  } else if (isOnUnreadView({ route: { name } })) {
    conversationType = 'unread';
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store
    .dispatch('assignPriority', { conversationId, priority })
    .then(() => {
      useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
        newValue: priority,
        from: 'Context menu',
      });
      useAlert(
        t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
          priority,
          conversationId,
        })
      );
    })
    .catch(() => {
      useAlert(t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.FAILED'));
    });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    useAlert(t('CONVERSATION.MARK_AS_UNREAD_FAILED'));
  }
}
// Ação DELIBERADA (botão "marcar como lida"), não leitura implícita: por isso
// `explicit`. Sem ele, conversa sem dono nunca mais ficaria lida — abrir e marcar
// batem no mesmo endpoint, e só o segundo deve limpar o marcador de conversa que
// não é sua. Ver `may_clear_unread_marker?`.
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
      explicit: true,
    });
  } catch (error) {
    useAlert(t('CONVERSATION.MARK_AS_READ_FAILED'));
  }
}

function leaveConversationIfAccessLost(team, conversationId) {
  // Conversation routes use either :conversation_id or :conversationId
  // depending on the view (conversation.routes.js).
  const openConversationId = Number(
    route.params.conversation_id ?? route.params.conversationId
  );
  if (!openConversationId || openConversationId !== Number(conversationId)) {
    return;
  }
  // An empty teams store right after a team assignment means it has not
  // loaded yet — fail open instead of ejecting a legitimate member.
  if (!teamsList.value.length) return;
  // assigneeId stays null: on a team change the backend clears or reassigns
  // a non-member assignee (ensure_assignee_is_from_team).
  // Times visíveis (membro ∪ supervisionado): o backend deixa o supervisor
  // continuar na conversa do time que ele supervisiona, então ejetá-lo aqui
  // seria o front discordando da regra do servidor.
  const lostAccess = losesConversationAccess({
    isAdmin: currentRole.value === 'administrator',
    userId: currentUser.value.id,
    userTeamIds: visibleTeams.value.map(visibleTeam => visibleTeam.id),
    teamId: team.id,
    assigneeId: null,
  });
  if (lostAccess) {
    useAlert(t('CONVERSATION.ASSIGNMENT_ACCESS_LOST'));
    router.push(`/app/accounts/${currentAccountId.value}/dashboard`);
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
      rethrow: true,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
    leaveConversationIfAccessLost(team, conversationId);
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(
  conversationId,
  status,
  snoozedUntil,
  customAttributes = null
) {
  const payload = {
    conversationId,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  });
}

async function handleAssignAndResolve({ teamId }) {
  const { conversationId, snoozedUntil } = assignResolveContext.value;
  closeAssignResolveDialog();
  try {
    // Mesmo encadeamento do botão da conversa: assume, checa os atributos
    // obrigatórios e só então encerra. Antes o gate encerrava direto.
    await assignSelfForResolve(conversationId, { teamId });

    const conversation = getConversationById.value(conversationId);
    const currentCustomAttributes = conversation?.custom_attributes || {};
    const contactCustomAttributes =
      conversation?.meta?.sender?.custom_attributes || {};
    const { hasMissing, missing } = checkMissingAttributes(
      currentCustomAttributes,
      contactCustomAttributes
    );

    if (hasMissing) {
      pendingRequiredAttributes.value = missing;
      resolveAttributesModalRef.value?.open(
        missing,
        { ...contactCustomAttributes, ...currentCustomAttributes },
        { id: conversationId, snoozedUntil }
      );
      return;
    }

    await toggleConversationStatus(
      conversationId,
      wootConstants.STATUS_TYPE.RESOLVED,
      snoozedUntil
    );
    useAlert(t('CONVERSATION.RESOLVE_ASSIGNMENT_GATE.SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION.CHANGE_STATUS_FAILED'));
  }
}

function handleResolveConversation(conversationId, status, snoozedUntil) {
  if (status !== wootConstants.STATUS_TYPE.RESOLVED) {
    toggleConversationStatus(conversationId, status, snoozedUntil);
    return;
  }

  // Check for required attributes before resolving
  const conversation = getConversationById.value(conversationId);

  // "Atribuição obrigatória": intercept non-assignee resolves with the dialog.
  if (requiresAssignmentToResolve(conversation)) {
    openAssignResolveDialog(conversation, snoozedUntil);
    return;
  }

  const currentCustomAttributes = conversation?.custom_attributes || {};
  const contactCustomAttributes =
    conversation?.meta?.sender?.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes,
    contactCustomAttributes
  );

  if (hasMissing) {
    // Pass conversation context through the modal's API
    const conversationContext = {
      id: conversationId,
      snoozedUntil,
    };
    pendingRequiredAttributes.value = missing;
    resolveAttributesModalRef.value?.open(
      missing,
      { ...contactCustomAttributes, ...currentCustomAttributes },
      conversationContext
    );
  } else {
    toggleConversationStatus(conversationId, status, snoozedUntil);
  }
}

async function handleResolveWithAttributes({ attributes, context }) {
  if (!context) return;

  const existingConversation = getConversationById.value(context.id);
  const currentCustomAttributes = existingConversation?.custom_attributes || {};
  const { conversation: conversationValues, contact: contactValues } =
    splitAttributesByModel(pendingRequiredAttributes.value, attributes);

  // Contato e conversa são chamadas distintas. Se a do contato falhar, a conversa
  // NÃO resolve — nada de encerrar em silêncio com o atributo vazio.
  if (Object.keys(contactValues).length) {
    const contactId = existingConversation?.meta?.sender?.id;
    if (!contactId) {
      useAlert(
        t(
          'CONVERSATION_WORKFLOW.REQUIRED_ATTRIBUTES.MODAL.ERRORS.CONTACT_MISSING'
        )
      );
      return;
    }

    try {
      await store.dispatch('contacts/update', {
        id: contactId,
        customAttributes: {
          ...(existingConversation?.meta?.sender?.custom_attributes || {}),
          ...contactValues,
        },
      });
    } catch (error) {
      useAlert(
        t(
          'CONVERSATION_WORKFLOW.REQUIRED_ATTRIBUTES.MODAL.ERRORS.CONTACT_UPDATE_FAILED'
        )
      );
      return;
    }
  }

  toggleConversationStatus(
    context.id,
    wootConstants.STATUS_TYPE.RESOLVED,
    context.snoozedUntil,
    Object.keys(conversationValues).length
      ? { ...currentCustomAttributes, ...conversationValues }
      : null
  );
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return getConversationById.value(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

function onOpenSearch() {
  showSearchModal.value = true;
}

function clearLocalSearchCache() {
  cachedSearchResults.value = [];
  cachedSearchQuery.value = '';
  store.dispatch('conversationSearch/clearSearchResults');
}

function onCloseSearch() {
  // Only clear if the modal was actually open (not triggered by Vue reactivity)
  if (showSearchModal.value) {
    showSearchModal.value = false;
    clearLocalSearchCache();
  }
}

function onSearch() {
  // Search is handled by ConversationSearchBar via store dispatch
}

function onClearSearch() {
  clearLocalSearchCache();
}

function onSelectSearchConversation(conversation) {
  const { accountId } = route.params;
  const { id } = conversation;

  // Navegar sem limpar resultados — usuário limpa manualmente com X ou Escape
  const url = conversationUrl({
    accountId,
    id,
    activeInbox: searchContext.value.inboxId,
    label: searchContext.value.label,
    teamId: searchContext.value.teamId,
    conversationType: searchContext.value.conversationType,
    foldersId: searchContext.value.foldersId,
  });

  router.push(frontendURL(url));
}

useEmitter('fetch_conversation_stats', () => {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  const filtersForMeta = { ...conversationFilters.value };
  delete filtersForMeta.assigneeType;
  store.dispatch('conversationStats/get', filtersForMeta);
});

onMounted(() => {
  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  resetAndFetchData();
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
  // AI tab eligibility: failure is tolerated (empty records ⇒ no AI tab;
  // an account without Captain must not break the chat list)
  if (isAssignmentGateEnabled.value) {
    store.dispatch('captainAssistants/get').catch(() => {});
  }
});

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);

async function deleteConversation() {
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('removeLabels', onRemoveLabels);
provide('updateConversationStatus', handleResolveConversation);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);

// Unattended/Unread routes: sync assignee tab from route (deep-link).
watch(
  () => [route.name, route.query],
  () => {
    if (
      route.name === 'conversation_unattended' ||
      UNREAD_ROUTES.includes(route.name)
    ) {
      const newTab = resolveAssigneeTab(
        route.name,
        route.query,
        permittedAssigneeTabKeys.value
      );
      const prevTab = activeAssigneeTab.value;
      activeAssigneeTab.value = newTab;
      if (prevTab !== newTab) {
        store.dispatch('conversationPage/reset');
        store.dispatch('emptyAllConversations');
        fetchConversations();
      }
    }
  },
  { deep: true }
);

// Single watcher for all view-level changes that require a full re-fetch.
// Consolidates 6 separate watchers into 1, preventing duplicate API calls
// when multiple props change simultaneously (e.g., route navigation).
watch(
  [
    () => props.conversationStatus,
    () => props.conversationInbox,
    () => props.label,
    () => props.conversationType,
    activeTeam,
    activeFolder,
  ],
  (
    [newStatus, , , newType, , newFolder],
    [prevStatus, , , prevType, , prevFolder]
  ) => {
    // Side effect: sync status filter
    if (newStatus !== prevStatus) {
      if (newStatus) {
        activeStatus.value = newStatus;
        store.dispatch('setChatStatusFilter', newStatus);
      } else {
        const { conversations_filter_by: filterBy = {} } = uiSettings.value;
        const { status } = filterBy;
        activeStatus.value = status || wootConstants.STATUS_TYPE.OPEN;
        store.dispatch('setChatStatusFilter', activeStatus.value);
      }
    }

    // Side effect: reset assignee tab when the view type changes.
    // Use the route-aware default so the Unread view keeps its "All" default
    // and the dedicated unassigned queue locks to "Unassigned" instead of being
    // forced back to "Mine". Clamp by permission, not tab-bar visibility.
    if (newType !== prevType && newType !== 'unattended') {
      activeAssigneeTab.value = resolveAssigneeTab(
        route.name,
        route.query,
        permittedAssigneeTabKeys.value
      );
    }

    // Side effect: sync active folder in store
    if (newFolder !== prevFolder) {
      store.dispatch(
        'customViews/setActiveConversationFolder',
        newFolder || null
      );
    }

    resetAndFetchData();
  }
);

// Dynamic tabs (gate ON) can disappear while active when their count drops to
// zero — filas antigas, "Rodízio", "Atenção", "IA" e "Não atribuídas". Isso
// deixaria activeTabIndex em -1, então voltamos para "Minhas". Não se aplica à
// visão de não-respondidas (abas incondicionais lá).
watch(
  [
    () => conversationStats.value.unAssignedCount,
    () => conversationStats.value.awaitingCount,
    () => conversationStats.value.rodizioCount,
    () => conversationStats.value.awaitingClosureCount,
    () => conversationStats.value.aiCount,
    isAssignmentGateEnabled,
    isRodizioTabsEnabled,
  ],
  () => {
    if (props.conversationType === 'unattended') return;
    if (!isAssignmentGateEnabled.value) return;
    if (!conversationStats.value.updatedOn) return;

    if (
      !assigneeTabItems.value.some(({ key }) => key === activeAssigneeTab.value)
    ) {
      updateAssigneeTab(wootConstants.ASSIGNEE_TYPE.ME);
    }
  }
);

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

watch(conversationFilters, newVal => {
  store.dispatch('updateChatListFilters', newVal);
});
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 conversations-list-wrap bg-n-surface-1"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout ? 'basis-full' : 'w-[380px] 2xl:w-[448px]',
    ]"
  >
    <slot />
    <ChatListHeader
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :active-status="activeStatus"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="chatListLoading && !conversationList.length"
      :search-context="searchContext"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
      @open-search="onOpenSearch"
      @search="onSearch"
      @clear-search="onClearSearch"
    />

    <TeleportWithDirection
      v-if="showAddFoldersModal"
      to="#saveFilterTeleportTarget"
    >
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </TeleportWithDirection>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <ChatTypeTabs
      v-if="!hasAppliedFiltersOrActiveFolders && !isSearchActive"
      :items="assigneeTabItems"
      :active-tab="activeAssigneeTab"
      is-compact
      @chat-tab-change="updateAssigneeTab"
    />

    <div
      v-if="!chatListLoading && !conversationList.length && !isSearchActive"
      class="flex flex-col overflow-auto justify-center items-center gap-3 p-4 text-center"
    >
      <template v-if="chatListError">
        <p class="text-n-slate-11">{{ $t('CHAT_LIST.LIST.LOAD_ERROR') }}</p>
        <Button
          :label="$t('CHAT_LIST.LIST.RETRY')"
          size="sm"
          @click="retryConversations"
        />
      </template>
      <p v-else>{{ $t('CHAT_LIST.LIST.404') }}</p>
    </div>
    <div
      v-else-if="showSearchNoResults"
      class="flex flex-col items-center justify-center p-8 overflow-auto text-center"
    >
      <div
        class="w-12 h-12 rounded-full bg-n-slate-3 dark:bg-n-solid-3 flex items-center justify-center mb-4"
      >
        <span class="i-lucide-search size-6 text-n-slate-11" />
      </div>
      <p class="text-base font-medium text-n-slate-12 mb-1">
        {{ $t('CONVERSATION.SEARCH.NO_RESULTS_TITLE') }}
      </p>
      <p class="text-sm text-n-slate-11 max-w-[280px]">
        {{
          conversationSearchQuery
            ? $t('CONVERSATION.SEARCH.NO_RESULTS_FOR_QUERY', {
                query: conversationSearchQuery,
              })
            : $t('CONVERSATION.SEARCH.NO_RESULTS')
        }}
      </p>
    </div>
    <ConversationBulkActions
      v-if="selectedConversations.length"
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      @select-all-conversations="toggleSelectAll"
      @assign-agent="onAssignAgent"
      @update-conversations="onUpdateConversations"
      @assign-labels="onAssignLabels"
      @assign-team="onAssignTeamsForBulk"
    />
    <TeamSelectionModal
      :show="showTransferTeamModal"
      :teams="teams"
      :title="$t('CONVERSATION.SELECT_TEAM_FOR_TRANSFER_TITLE')"
      :description="$t('CONVERSATION.SELECT_TEAM_FOR_TRANSFER_DESCRIPTION')"
      @confirm="onConfirmAgentTransferTeam"
      @close="cancelAgentTransferTeamSelection"
    />
    <AssignAndResolveDialog
      :show="showBulkAssignResolveDialog"
      is-bulk
      :bulk-count="bulkAssignResolveCount"
      :requires-team="bulkAssignResolveRequiresTeam"
      :teams="myTeams"
      @confirm="onConfirmBulkAssignResolve"
      @close="onCancelBulkAssignResolve"
    />
    <div
      ref="conversationListRef"
      class="flex-1 min-h-0 overflow-y-auto conversations-list"
      :class="{ '!overflow-hidden': isContextMenuOpen }"
    >
      <Virtualizer
        ref="virtualListRef"
        v-slot="{ item, index }"
        :data="conversationList"
      >
        <ConversationItem
          :source="item"
          :label="label"
          :team-id="teamId"
          :folders-id="foldersId"
          :conversation-type="conversationType"
          :conversation-status="conversationStatus"
          :show-assignee="showAssigneeInConversationCard"
          :data-index="index"
          @select-conversation="selectConversation"
          @de-select-conversation="deSelectConversation"
        />
      </Virtualizer>
      <div
        v-if="
          (isSearchActive &&
            store.getters['conversationSearch/getUIFlags']?.conversation
              ?.isFetching) ||
          (!isSearchActive && chatListLoading)
        "
        class="flex justify-center my-4"
      >
        <Spinner class="text-n-brand" />
      </div>
      <p
        v-else-if="showEndOfListMessage && !isSearchActive"
        class="p-4 text-center text-n-slate-11"
      >
        {{ $t('CHAT_LIST.EOF') }}
      </p>
      <div
        v-else-if="chatListError && conversationList.length && !isSearchActive"
        class="flex flex-col items-center gap-2 p-4 text-center"
      >
        <p class="text-sm text-n-slate-11">
          {{ $t('CHAT_LIST.LIST.LOAD_ERROR') }}
        </p>
        <Button
          :label="$t('CHAT_LIST.LIST.RETRY')"
          size="sm"
          @click="retryConversations"
        />
      </div>
      <IntersectionObserver
        v-else-if="!chatListError"
        :options="intersectionObserverOptions"
        @observed="loadMoreConversations"
      />
    </div>
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="selectedConversationId = null"
    />
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>
    <ConversationSearchModal
      :is-open="showSearchModal"
      :context="searchContext"
      @close="onCloseSearch"
      @select-conversation="onSelectSearchConversation"
    />
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
    <AssignAndResolveDialog
      :show="showAssignResolveDialog"
      :variant="assignResolveContext.variant"
      :assignee-name="assignResolveContext.assigneeName"
      :requires-team="assignResolveContext.requiresTeam"
      :teams="myTeams"
      @confirm="handleAssignAndResolve"
      @close="closeAssignResolveDialog"
    />
  </div>
</template>
